import {
  Button,
  Card,
  CardContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tinct/web-components';
import { AnalyzeCsvResponse, CsvMapping } from '@tinct/core-api-models';
import { pluralize } from '@tinct/core-utils';
import {
  SparklesIcon,
  ArrowRightIcon,
  CheckIcon,
  CircleCheckIcon,
} from 'lucide-react';

// Mapping field configuration
const MAPPING_FIELDS = [
  { key: 'company.name', label: 'Company Name', required: true },
  { key: 'company.website', label: 'Company URL', required: true },
] as const;

// Field mapping row component
type FieldMappingRowProps = {
  fieldLabel: string;
  required?: boolean;
  columns: string[];
  selectedColumn: string | undefined;
  onColumnChange: (column: string) => void;
};

const FieldMappingRow = ({
  fieldLabel,
  required,
  columns,
  selectedColumn,
  onColumnChange,
}: FieldMappingRowProps) => {
  const isMatched = !!selectedColumn;

  return (
    <div className="bg-muted rounded-lg p-2.5 flex items-center gap-2.5">
      <div className="flex items-center gap-3 flex-shrink-0">
        <p className="text-sm font-medium w-[246px]">
          {fieldLabel} {required && '*'}
        </p>
      </div>
      <ArrowRightIcon
        className={`size-6 flex-shrink-0 ${isMatched ? 'text-emerald-400' : 'text-muted-foreground'}`}
      />
      <div className="flex-1 flex items-center justify-between">
        <Select value={selectedColumn || ''} onValueChange={onColumnChange}>
          <SelectTrigger className="w-[248px]">
            <SelectValue placeholder="Select column..." />
          </SelectTrigger>
          <SelectContent>
            {columns
              .map((column, index) => ({ column, index }))
              .filter(({ column }) => column !== '')
              .map(({ column, index }) => (
              <SelectItem key={column} value={column}>
                {column} (column {String.fromCharCode(65 + index)})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isMatched && <CheckIcon className="size-6 text-emerald-400" />}
      </div>
    </div>
  );
};

// Mapping step component
export type MappingStepProps = {
  analysisResult: AnalyzeCsvResponse;
  mapping: CsvMapping;
  onMappingChange: (mapping: CsvMapping) => void;
  onBack: () => void;
  onContinue: () => void;
};

export const MappingStep = ({
  analysisResult,
  mapping,
  onMappingChange,
  onBack,
  onContinue,
}: MappingStepProps) => {
  const columns = (analysisResult.columns as string[]) || [];
  const companyCount =
    (analysisResult.stats as { companiesCount?: number })?.companiesCount || 0;

  const handleColumnChange = (fieldKey: string, column: string) => {
    onMappingChange({
      ...mapping,
      [fieldKey]: column,
    });
  };

  // Check if required fields are mapped
  const hasRequiredMappings = MAPPING_FIELDS.filter((f) => f.required).every(
    (field) => mapping[field.key]
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Map CSV columns</DialogTitle>
        <DialogDescription>
          Match your CSV columns to the required fields.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 mt-4">
        {/* Success alert */}
        <div className="bg-teal-50 text-teal-800 rounded-lg px-4 py-3 flex items-start gap-3">
          <CircleCheckIcon className="size-4 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium">File analyzed successfully</p>
            <p className="text-sm">
              {companyCount} {pluralize(companyCount, 'company', 'companies')}{' '}
              found
            </p>
          </div>
        </div>

        {/* Mapping card */}
        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Header */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <p className="text-xl font-semibold">Smart field mapping</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border-2 border-fuchsia-300 bg-white text-xs font-semibold">
                  <SparklesIcon className="size-3" />
                  Auto matched
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Match Tinct fields with your CSV columns
              </p>
            </div>

            {/* Column headers */}
            <div className="flex items-start gap-6 text-sm text-muted-foreground">
              <p className="flex-1">Tinct</p>
              <p className="w-[326px]">CSV column</p>
            </div>

            {/* Mapping rows */}
            <div className="space-y-6">
              {MAPPING_FIELDS.map((field) => (
                <FieldMappingRow
                  key={field.key}
                  fieldLabel={field.label}
                  required={field.required}
                  columns={columns}
                  selectedColumn={mapping[field.key] as string | undefined}
                  onColumnChange={(column) =>
                    handleColumnChange(field.key, column)
                  }
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <DialogFooter className="sm:justify-between mt-6">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={!hasRequiredMappings}
          onClick={onContinue}
        >
          Import
        </Button>
      </DialogFooter>
    </>
  );
};
